import { useId } from 'react';
import type { ReactNode } from 'react';

const ink = '#f0f2eb';
const muted = '#b2c3bd';
const accent = '#c9f58a';
const blue = '#a8d9f1';
const warm = '#e9b28f';

type DiagramProps = { compact: boolean; id: string };
function Label({ x, y, children, size = 26, color = ink, anchor = 'middle', weight = 600 }: { x: number; y: number; children: ReactNode; size?: number; color?: string; anchor?: 'middle' | 'start' | 'end'; weight?: number }) {
  return <text x={x} y={y} fill={color} fontSize={size} fontWeight={weight} textAnchor={anchor}>{children}</text>;
}
function Line({ d, color = accent, dashed = false }: { d: string; color?: string; dashed?: boolean }) {
  return <path d={d} fill="none" stroke={color} strokeWidth="2.5" strokeOpacity=".7" strokeDasharray={dashed ? '7 9' : undefined} />;
}
function Prism({ x, y, r = 70 }: { x: number; y: number; r?: number }) {
  return <g transform={`translate(${x} ${y})`}><path d={`M0 ${-r} L${r} ${r * .55} L${-r} ${r * .55} Z`} fill="#c9f58a" fillOpacity=".07" stroke={accent} strokeWidth="2" /><path d={`M0 ${-r} L0 ${r * .12} M${-r} ${r * .55} L0 ${r * .12} L${r} ${r * .55}`} fill="none" stroke={accent} strokeWidth="1" opacity=".5" /></g>;
}
function Portrait({ x, y, initials, src, r = 35, clip }: { x: number; y: number; initials: string; src?: string; r?: number; clip: string }) {
  return <g><circle cx={x} cy={y} r={r + 4} fill="#1b302d" stroke={accent} strokeWidth="1.5" /><Label x={x} y={y + 9} size={26}>{initials}</Label>{src && <><defs><clipPath id={clip}><circle cx={x} cy={y} r={r} /></clipPath></defs><image href={src} x={x - r} y={y - r} width={r * 2} height={r * 2} preserveAspectRatio="xMidYMid slice" clipPath={`url(#${clip})`} /></>}</g>;
}

function Origin({ compact, id }: DiagramProps) {
  if (compact) return <>
    <Line d="M76 52 V560" />
    {[90, 235, 395, 550].map(y => <circle key={y} cx="76" cy={y} r="7" fill={accent} />)}
    <Label x={115} y={64} anchor="start" size={22} color={accent}>DÉBUT OCTOBRE 2025</Label>
    <Portrait x={150} y={114} initials="JP" src="/JP.png" clip={`${id}-jp`} r={28} />
    <Line d="M190 114 H240" /><Portrait x={280} y={114} initials="DB" src="/DB.png" clip={`${id}-db`} r={28} />
    <Label x={115} y={170} anchor="start" size={26}>JP appelle Dany : charge de projet</Label>
    <Label x={115} y={233} anchor="start" size={22} color={accent}>14 OCTOBRE 2025</Label>
    <Label x={115} y={277} anchor="start" size={30}>Formation. Connaissances.</Label>
    <Label x={115} y={375} anchor="start" size={38}>AIKUB → PRIsME</Label>
    <Label x={115} y={418} anchor="start" size={25} color={muted}>PRIME + s · notre vision systémique</Label>
    <Label x={115} y={546} anchor="start" size={22} color={accent}>SEPTEMBRE 2026</Label>
    <Label x={115} y={590} anchor="start" size={32}>À nous d’écrire la suite.</Label>
  </>;
  return <>
    <Line d="M75 210 H940" />
    {[105, 340, 625, 900].map(x => <circle key={x} cx={x} cy="210" r="8" fill={accent} />)}
    <Label x={105} y={60} size={22} color={accent}>DÉBUT OCT. 2025</Label>
    <Portrait x={72} y={133} initials="JP" src="/JP.png" clip={`${id}-jp`} r={28} />
    <Portrait x={151} y={133} initials="DB" src="/DB.png" clip={`${id}-db`} r={28} />
    <Label x={105} y={260}>JP → Dany</Label><Label x={105} y={300} size={23} color={muted}>Charge de projet</Label>
    <Label x={340} y={140} size={22} color={accent}>14 OCT. 2025</Label>
    <Label x={340} y={260}>Formation</Label><Label x={340} y={300} size={23} color={muted}>Connaissances</Label>
    <Label x={625} y={140} size={30}>AIKUB → PRIsME</Label>
    <Label x={625} y={260}>PRIME + s</Label><Label x={625} y={300} size={23} color={muted}>L’ADN BZ, tous les angles</Label>
    <Label x={900} y={140} size={22} color={accent}>SEPT. 2026</Label>
    <Label x={900} y={260}>La suite,</Label><Label x={900} y={299}>ensemble.</Label>
    <Label x={500} y={398} size={22} color={muted}>Le nom évolue. L’intention reste : simplifier le compliqué.</Label>
  </>;
}

function Reference({ compact }: DiagramProps) {
  const cx = compact ? 300 : 500;
  const cy = compact ? 130 : 180;
  const lx = compact ? 155 : 180, rx = compact ? 445 : 820;
  const by = compact ? 350 : 200;
  return <>
    <Line d={`M${cx - 60} ${cy} Q${lx} ${cy} ${lx} ${by}`} />
    <Line d={`M${cx + 60} ${cy} Q${rx} ${cy} ${rx} ${by}`} color={blue} />
    <circle cx={cx} cy={cy} r={compact ? 91 : 105} fill="#132b25" stroke={accent} strokeWidth="1.5" />
    <Prism x={cx} y={cy - 14} r={42} />
    <Label x={cx} y={cy + 38} size={32}>Master</Label>
    <Label x={cx} y={compact ? 268 : 35} size={23} color={muted}>Référence commune · accès gouvernés</Label>
    <circle cx={lx} cy={by} r="12" fill={accent} /><circle cx={rx} cy={by} r="12" fill={blue} />
    <Label x={lx} y={by + 54} size={36}>Client</Label>
    <Label x={rx} y={by + 54} size={36}>Employé</Label>
    <Label x={lx} y={by + 98} size={27} color={accent}>Cadre BZ</Label>
    <Label x={rx} y={by + 98} size={27} color={blue}>Compétences</Label>
    {(compact ? [['Contexte', 'Besoins', 'Priorités'], ['Forces', 'Rôle', 'Développement']] : [['Contexte · besoins', 'priorités'], ['Forces · rôle', 'développement']]).map((labels, branch) => labels.map((label, i) => <Label key={label} x={branch ? rx : lx} y={by + 140 + i * 33} size={23} color={muted}>{label}</Label>))}
    <Label x={cx} y={compact ? 630 : 425} size={compact ? 21 : 22} color={muted}>Des espaces privés distincts. Pas un partage sans limites.</Label>
  </>;
}

function Memory({ compact }: DiagramProps) {
  const sourceLabels = ['Documents', 'Décisions', 'Cadres', 'Standards', 'Terrain'];
  if (compact) return <>
    {sourceLabels.map((label, i) => <g key={label}><Label x={110 + (i % 3) * 190} y={50 + Math.floor(i / 3) * 70} size={24} color={muted}>{label}</Label><Line d={`M${110 + (i % 3) * 190} ${62 + Math.floor(i / 3) * 70} Q300 180 300 215`} /></g>)}
    <Prism x={300} y={255} r={80} /><Label x={300} y={275} size={33}>Master</Label>
    <Label x={300} y={346} size={25} color={accent}>Documenté · Validation humaine</Label>
    <Line d="M300 365 V565" />
    <Label x={300} y={416} size={29}>Question</Label>
    <rect x="88" y="447" width="424" height="62" rx="31" fill="#132b25" stroke={accent} />
    <Label x={300} y={486} size={26}>Recherche dans les sources</Label>
    <Label x={300} y={560} size={29}>Réponse appuyée</Label>
    <Label x={300} y={607} size={23} color={accent}>Sources citées · droits respectés</Label>
  </>;
  return <>
    {sourceLabels.map((label, i) => <g key={label}><Label x={85 + (i % 2) * 25} y={48 + i * 70} size={25} color={muted}>{label}</Label><Line d={`M195 ${40 + i * 70} Q280 ${40 + i * 70} 350 195`} /></g>)}
    <Prism x={415} y={192} r={105} /><Label x={415} y={220} size={36}>Master</Label>
    <Label x={415} y={307} size={24} color={accent}>Documenté</Label>
    <Label x={415} y={342} size={23} color={muted}>Validation humaine</Label>
    <Line d="M518 200 H625" />
    <Line d="M775 75 V320" />
    <Label x={775} y={57} size={28}>Question</Label>
    <rect x="590" y="148" width="380" height="110" rx="55" fill="#132b25" stroke={accent} />
    <Label x={780} y={193} size={27}>Recherche</Label><Label x={780} y={229} size={25}>dans les sources</Label>
    <Label x={775} y={328} size={28}>Réponse appuyée</Label>
    <Label x={775} y={370} size={23} color={accent}>Sources citées</Label>
    <Label x={500} y={424} size={22} color={muted}>La bonne source, pour la bonne personne. Les droits d’accès s’appliquent.</Label>
  </>;
}


function Adoption({ compact, id }: DiagramProps) {
  const people = [
    { name: 'Éric', initials: 'EH', src: '/EH.png' },
    { name: 'Jean-Michel', initials: 'JM', src: '/JMC.png' },
    { name: 'Francis', initials: 'FM', src: '/FM.png' },
    { name: 'Stéphane', initials: 'SP', src: '/SP.png' },
  ];
  return <>
    {people.map((person, i) => {
      const x = compact ? 78 + i * 148 : 100 + i * 155;
      const y = compact ? 95 : 115;
      return <g key={person.name}><Portrait x={x} y={y} initials={person.initials} src={person.src} clip={`${id}-${i}`} r={i === 0 ? 43 : 34} /><Label x={x} y={y + 76} size={28}>{person.name}</Label><Line d={`M${x} ${y + 94} Q${x} ${compact ? 240 : 280} ${compact ? 300 : 350} ${compact ? 270 : 290}`} /></g>;
    })}
    <Label x={compact ? 300 : 350} y={compact ? 286 : 310} size={27} color={accent}>Construire · tester · documenter</Label>
    <Line d={compact ? 'M300 315 V420' : 'M660 140 Q780 170 775 265'} dashed />
    <path d={compact ? 'M60 350 H190 M235 350 H365 M410 350 H540' : 'M710 85 V150 M710 190 V255 M710 295 V360'} stroke={warm} strokeWidth="7" />
    <Label x={compact ? 300 : 850} y={compact ? 395 : 95} size={24} color={warm}>Les habitudes résistent.</Label>
    <Label x={compact ? 300 : 850} y={compact ? 457 : 225} size={compact ? 40 : 34}>Nous tous</Label>
    <Label x={compact ? 300 : 850} y={compact ? 500 : 274} size={24} color={muted}>Essayer · soutenir</Label>
    <Label x={compact ? 300 : 850} y={compact ? 536 : 311} size={24} color={muted}>Faire un retour · ajuster</Label>
    <Line d={compact ? 'M510 485 Q580 580 450 595 H150 Q25 595 45 245' : 'M910 340 Q920 410 590 397 H240 Q45 397 45 240'} color={blue} dashed />
    <Label x={compact ? 300 : 490} y={compact ? 633 : 436} size={22} color={blue}>L’adoption avance avec nous, pas contre nous.</Label>
  </>;
}

function Learning({ compact }: DiagramProps) {
  const cx = compact ? 300 : 500, cy = compact ? 326 : 250;
  const rx = compact ? 205 : 300, ry = compact ? 165 : 132;
  const points = ['Apprendre', 'Appliquer', 'Documenter', 'Remettre en question', 'Améliorer'].map((label, i) => {
    const a = (-90 + i * 72) * Math.PI / 180;
    return { label, x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry };
  });
  return <>
    <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={accent} strokeWidth="2" strokeDasharray="4 9" />
    {points.map(({ label, x, y }, i) => <g key={label}>
      <circle cx={x} cy={y} r="8" fill={accent} />
      <rect x={x - (i === 3 ? 139 : 89)} y={y - 45} width={i === 3 ? 278 : 178} height="33" fill="#0c1d1a" />
      <Label x={x} y={y - 20} size={compact ? 24 : 27}>{label}</Label>
    </g>)}
    <Label x={cx} y={cy - 20} size={compact ? 27 : 31} color={accent}>VOLONTÉ</Label>
    <Label x={cx} y={cy + 22} size={compact ? 27 : 31} color={accent}>D’APPRENDRE</Label>
    <Label x={cx} y={compact ? 73 : 38} size={25} color={muted}>L’expérience est un point d’appui.</Label>
    <Label x={cx} y={compact ? 575 : 434} size={compact ? 28 : 29} color={warm}>DEUIL DE NOS CERTITUDES</Label>
    {compact && <Label x={cx} y={621} size={24} color={muted}>Pas de notre valeur.</Label>}
  </>;
}

function Alignment({ compact }: DiagramProps) {
  const departments = ['Ventes', 'TI / SAC', 'Cloud', 'Cyber', 'Solutions d’affaires', 'Programmation', 'Télécom', 'Admin / Finance / RH'];
  if (compact) return <>
    <Label x={300} y={32} size={24} color={warm}>L’IA SANS ALIGNEMENT</Label>
    {[0, 1, 2, 3, 4].map(i => <Line key={i} d={`M${65 + i * 50} 75 C${540 - i * 45} 165 ${90 + i * 35} 10 ${390 + i * 30} 160`} color={warm} />)}
    <Label x={300} y={213} size={27} color={accent}>AVEC UNE RÉFÉRENCE COMMUNE</Label>
    <Prism x={300} y={407} r={64} /><Label x={300} y={422} size={30}>PRIsME</Label>
    {departments.map((label, i) => {
      const left = i < 4, x = left ? 115 : 480, y = 290 + (i % 4) * 96;
      return <g key={label}><Line d={`M${left ? 180 : 420} ${y} Q${left ? 230 : 370} ${y} ${left ? 242 : 358} 410`} /><circle cx={left ? 180 : 420} cy={y} r="4" fill={accent} /><Label x={x} y={y - 13} size={i === 7 ? 18 : 21}>{label}</Label></g>;
    })}
    <Label x={300} y={647} size={20} color={muted}>Vision stratégique · pas une intégration annoncée</Label>
  </>;
  return <>
    <Label x={200} y={35} size={23} color={warm}>L’IA SANS ALIGNEMENT</Label>
    {[0, 1, 2, 3, 4, 5].map(i => <Line key={i} d={`M40 ${90 + i * 43} C${380 - i * 24} ${405 - i * 48} ${70 + i * 23} ${20 + i * 34} 370 ${120 + i * 39}`} color={warm} />)}
    <Label x={200} y={416} size={25} color={warm}>Le chaos, plus vite.</Label>
    <Line d="M420 60 V375" color={muted} dashed />
    <Label x={705} y={35} size={23} color={accent}>UNE RÉFÉRENCE COMMUNE</Label>
    <Prism x={710} y={215} r={74} /><Label x={710} y={236} size={30}>PRIsME</Label>
    {departments.map((label, i) => {
      const left = i < 4, x = left ? 535 : 887, y = 100 + (i % 4) * 80;
      return <g key={label}><Line d={`M${left ? 550 : 870} ${y + 9} Q${left ? 620 : 800} ${y + 9} ${left ? 650 : 770} 220`} /><Label x={x} y={y} size={i === 7 ? 20 : 23}>{label}</Label></g>;
    })}
    <Label x={710} y={416} size={24} color={accent}>Toute l’organisation. Une direction.</Label>
  </>;
}

const diagrams = [Origin, Reference, Memory, Adoption, Learning, Alignment];
export default function PrismeStoryDiagrams({ scene, compact, description }: { scene: number; compact: boolean; description: string }) {
  const id = useId().replace(/:/g, '');
  const Diagram = diagrams[scene];
  return <svg className="prisme-story-diagram" viewBox={compact ? '0 0 600 670' : '0 0 1000 460'} role="img" aria-labelledby={`${id}-description`}>
    <title id={`${id}-description`}>{description}</title>
    <Diagram compact={compact} id={id} />
  </svg>;
}
